import { FC, useState, useMemo } from 'react'
import { LineChart } from '@mui/x-charts'
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from '../../../../components/ui/card'
import { ProcessedData } from './types'
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs'
import { Button } from '../../../../components/ui/button'
import moment from 'moment'
import { calculateStats, detectAnomalies } from './utils'

interface GraphComponentProps {
    data: ProcessedData
    displayNameMap?: Record<string, string>
}

const GraphComponent: FC<GraphComponentProps> = ({
    data,
    displayNameMap = {},
}) => {
    const getDisplayName = (key: string) => displayNameMap[key] || key
    const [chartType, setChartType] = useState<'line' | 'area'>('line')
    const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('24h')

    // Calculate global time range for all visible charts
    const globalTimeRange = useMemo(() => {
        let minTime = Number.MAX_SAFE_INTEGER
        let maxTime = 0
        Object.values(data).forEach((series) => {
            const timestamps = series.timestamps
            if (timestamps.length > 0) {
                minTime = Math.min(minTime, timestamps[0].getTime())
                maxTime = Math.max(
                    maxTime,
                    timestamps[timestamps.length - 1].getTime()
                )
            }
        })
        return {
            min:
                minTime !== Number.MAX_SAFE_INTEGER
                    ? minTime
                    : new Date().getTime() - 86400000,
            max: maxTime !== 0 ? maxTime : new Date().getTime(),
        }
    }, [data])

    // Safely convert a value to a Date, returning null if invalid
    const toDate = (value: Date | number | null | undefined): Date | null => {
        if (value == null) return null
        const d = typeof value === 'number' ? new Date(value) : value
        return d instanceof Date && !isNaN(d.getTime()) ? d : null
    }

    const formatXAxisTime = (value: Date | number | null | undefined) => {
        const d = toDate(value)
        if (!d) return ''
        return moment(d).format('HH:mm')
    }

    // FIX: formatTooltipTime was referenced but never defined — defined here
    const formatTooltipTime = (value: Date | number | null | undefined) => {
        const d = toDate(value)
        if (!d) return 'Invalid date'
        return moment(d).format('D/M/YY HH:mm:ss')
    }

    return (
        <div className="space-y-4">
            {Object.keys(data).length === 0 ? (
                <Card className="p-6 text-center">
                    <p>
                        No data available for the selected metrics or time
                        period.
                    </p>
                </Card>
            ) : (
                Object.keys(data).map((key) => {
                    const { timestamps, values } = data[key]
                    const stats = calculateStats(values)
                    const anomalies = detectAnomalies(values)

                    // Filter out any invalid timestamps (defensive)
                    const validPairs = timestamps
                        .map((ts, i) => ({ ts, v: values[i] }))
                        .filter(
                            ({ ts }) =>
                                ts instanceof Date && !isNaN(ts.getTime())
                        )
                    const cleanTimestamps = validPairs.map((p) => p.ts)
                    const cleanValues = validPairs.map((p) => p.v)

                    const firstTs = cleanTimestamps[0]
                    const lastTs = cleanTimestamps[cleanTimestamps.length - 1]
                    const durationMinutes =
                        firstTs && lastTs
                            ? (lastTs.getTime() - firstTs.getTime()) / 60000
                            : 0

                    const seriesConfig = {
                        data: cleanValues,
                        label: getDisplayName(key),
                        curve: 'stepAfter' as const,
                        showMark: cleanValues.length < 30,
                        valueFormatter: (value: number | null) =>
                            value != null ? `${value}` : 'null',
                    }

                    const xAxisConfig = [
                        {
                            data: cleanTimestamps,
                            scaleType: 'time' as const,
                            valueFormatter: formatXAxisTime,
                            min: globalTimeRange.min,
                            max: globalTimeRange.max,
                            tickLabelStyle: { fontSize: 12 },
                        },
                    ]

                    const yAxisConfig = [{ label: getDisplayName(key) }]

                    const chartMargin = {
                        top: 10,
                        right: 10,
                        bottom: 35,
                        left: 50,
                    }

                    return (
                        <Card key={key} className="mb-4">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                    <CardTitle className="text-xl text-primary">
                                        {getDisplayName(key)}
                                    </CardTitle>
                                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                                            Current:{' '}
                                            <strong>{stats.current}</strong>
                                        </span>
                                        <span className="px-2 py-1 bg-green-100 rounded text-sm">
                                            Min: <strong>{stats.min}</strong>
                                        </span>
                                        <span className="px-2 py-1 bg-red-100 rounded text-sm">
                                            Max: <strong>{stats.max}</strong>
                                        </span>
                                        <span className="px-2 py-1 bg-blue-100 rounded text-sm">
                                            Avg: <strong>{stats.avg}</strong>
                                        </span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {cleanValues.length > 0 ? (
                                    <div className="h-80">
                                        <LineChart
                                            xAxis={xAxisConfig}
                                            yAxis={yAxisConfig}
                                            series={[
                                                {
                                                    ...seriesConfig,
                                                    area: chartType === 'area',
                                                },
                                            ]}
                                            height={300}
                                            margin={chartMargin}
                                            tooltip={{
                                                axisValueFormatter: (date) =>
                                                    formatXAxisTime(date, {
                                                        location: 'tooltip',
                                                    }),
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center py-10">
                                        No data points available for this
                                        metric.
                                    </div>
                                )}

                                {data[key].mins && data[key].maxes && (
                                    <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                                        <p className="font-medium mb-1">
                                            Data Stats:
                                        </p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                                            <span>
                                                Current:{' '}
                                                <strong>{stats.current}</strong>
                                            </span>
                                            <span>
                                                Min:{' '}
                                                <strong>
                                                    {Math.min(
                                                        ...(data[key].mins ||
                                                            [])
                                                    )}
                                                </strong>
                                            </span>
                                            <span>
                                                Max:{' '}
                                                <strong>
                                                    {Math.max(
                                                        ...(data[key].maxes ||
                                                            [])
                                                    )}
                                                </strong>
                                            </span>
                                            <span>
                                                Avg:{' '}
                                                <strong>{stats.avg}</strong>
                                            </span>
                                            {data[key].counts && (
                                                <span>
                                                    Total Points:{' '}
                                                    <strong>
                                                        {data[
                                                            key
                                                        ].counts!.reduce(
                                                            (sum, c) => sum + c,
                                                            0
                                                        )}
                                                    </strong>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {cleanTimestamps.length > 0 && (
                                    <div className="mt-4 text-xs text-gray-500">
                                        <div>
                                            Data range:{' '}
                                            {moment(firstTs).format(
                                                'MMM D, YYYY HH:mm:ss'
                                            )}{' '}
                                            to{' '}
                                            {moment(lastTs).format(
                                                'MMM D, YYYY HH:mm:ss'
                                            )}
                                        </div>
                                        <div>
                                            Total data points:{' '}
                                            {cleanValues.length}
                                        </div>
                                        <div>
                                            Sample rate:{' '}
                                            {cleanValues.length > 1 &&
                                            durationMinutes > 0
                                                ? `~${Math.round(cleanValues.length / durationMinutes)} points/minute`
                                                : 'N/A'}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )
                })
            )}
        </div>
    )
}

export default GraphComponent
